<template>
  <div class="fixed-relation">
    <div id="tencent-map" class="tencent-map"></div>
  </div>
</template>

<script>

const appKey = process.env.VUE_APP_TENCENTMAP_KEY;

export default {
  props: {
    // form-create 传入的值：{ oldLatitude, oldLongitude, newLatitude, newLongitude, distance }
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      TMap: null,
      map: null,
      overlays: [], // 已绘制的覆盖物，重绘时清除
    };
  },
  watch: {
    value: {
      deep: true,
      handler(val) {
        this.drawMarkers(val);
      },
    },
  },
  methods: {
    /** @desc  异步加载腾讯地图 */
    syncLoadTMap() {
      return new Promise((resolve, reject) => {
        if (window.qq && window.qq.maps) {
          this.TMap = window.qq.maps;
          resolve(window.qq.maps);
        } else {
          const scriptMap = document.createElement('script');
          scriptMap.type = 'text/javascript';
          scriptMap.async = true;
          scriptMap.src = `https://map.qq.com/api/js?v=2.exp&key=${appKey}&callback=initTencentMap`;
          scriptMap.onerror = reject;
          scriptMap.dataTitle = 'tencentMap';
          document.body.appendChild(scriptMap);

          window.initTencentMap = () => {
            this.TMap = window.qq.maps;
            resolve(window.qq.maps);
          };
        }
      });
    },
    /** @desc 初始化地图 */
    initMap() {
      if (!this.TMap) return;
      this.map = new this.TMap.Map(document.getElementById('tencent-map'), {
        zoom: 16,
        scrollwheel: true, // 允许滚轮缩放
        zoomControl: true,
        center: new this.TMap.LatLng(39.984104, 116.307503),
      });
      this.drawMarkers(this.value);
    },
    /** @desc 生成带文字的大头针图标（SVG data URI，文字嵌在针头内） */
    createPinIcon(text, bgColor) {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="42" viewBox="0 0 52 62">
        <path d="M26 2C13.3 2 3 12.3 3 25c0 16.5 23 35 23 35s23-18.5 23-35C49 12.3 38.7 2 26 2z" fill="${bgColor}" stroke="#ffffff" stroke-width="4"/>
        <text x="26" y="26" text-anchor="middle" dominant-baseline="central" fill="#ffffff" font-size="14" font-family="PingFang SC, Microsoft YaHei, sans-serif" font-weight="600">${text}</text>
      </svg>`;
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      // 锚点设在针尖（底部中心），让针尖指向实际坐标
      return new this.TMap.MarkerImage(url, new this.TMap.Size(36, 42), new this.TMap.Point(0, 0), new this.TMap.Point(18, 41));
    },
    /** @desc 创建大头针点位（彩针白字） */
    createMarker(latLng, text, bgColor) {
      const marker = new this.TMap.Marker({
        position: latLng,
        map: this.map,
        icon: this.createPinIcon(text, bgColor),
      });
      this.overlays.push(marker);
    },
    /** @desc 清除已绘制覆盖物 */
    clearOverlays() {
      this.overlays.forEach((o) => o.setMap(null));
      this.overlays = [];
    },
    /** @desc 绘制原地址/新地址点位、虚线和距离标注（仅有新地址时只标注新地址） */
    drawMarkers(mapInfo) {
      if (!this.map || !mapInfo) return;
      const {
        oldLatitude, oldLongitude, newLatitude, newLongitude, distance,
      } = mapInfo;
      const hasOld = oldLatitude && oldLongitude;
      const hasNew = newLatitude && newLongitude;
      if (!hasOld && !hasNew) return;
      this.clearOverlays();
      // 只有旧地址：只标注旧地址
      if (!hasNew) {
        const onlyOldLatLng = new this.TMap.LatLng(Number(oldLatitude), Number(oldLongitude));
        this.createMarker(onlyOldLatLng, '原地址', '#07C160');
        this.map.setCenter(onlyOldLatLng);
        this.map.setZoom(16);
        return;
      }
      // 新地址：橘色背景白色文字
      const newLatLng = new this.TMap.LatLng(Number(newLatitude), Number(newLongitude));
      this.createMarker(newLatLng, '新地址', '#FF8F1F');
      // 只有新地址时：不画旧地址/虚线/距离，视野定位到新地址
      if (!hasOld) {
        this.map.setCenter(newLatLng);
        this.map.setZoom(16);
        return;
      }
      // 原地址：绿色背景白色文字
      const oldLatLng = new this.TMap.LatLng(Number(oldLatitude), Number(oldLongitude));
      this.createMarker(oldLatLng, '原地址', '#07C160');
      // 两点虚线连接
      const polyline = new this.TMap.Polyline({
        path: [oldLatLng, newLatLng],
        map: this.map,
        strokeColor: '#666666',
        strokeWeight: 2,
        strokeDashStyle: 'dash',
      });
      this.overlays.push(polyline);
      // 虚线中点标注距离（深色圆角胶囊，居中盖在虚线上）
      const midLatLng = new this.TMap.LatLng(
        (oldLatLng.getLat() + newLatLng.getLat()) / 2,
        (oldLatLng.getLng() + newLatLng.getLng()) / 2,
      );
      if (distance !== undefined && distance !== null && distance !== '') {
        const distanceLabel = new this.TMap.Label({
          content: `${distance}km`,
          position: midLatLng,
          map: this.map,
          offset: new this.TMap.Size(-30, -12), // 近似居中于中点
          style: {
            color: '#595959',
            fontSize: '14px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
          },
        });
        this.overlays.push(distanceLabel);
      }
      // 视野缩放到能展示两个点
      const bounds = new this.TMap.LatLngBounds();
      bounds.extend(oldLatLng);
      bounds.extend(newLatLng);
      this.map.fitBounds(bounds);
    },
  },
  async mounted() {
    await this.syncLoadTMap();
    this.initMap();
  },
  beforeDestroy() {
    this.clearOverlays();
    this.TMap = null;
    this.map = null;
  },
};
</script>

<style lang="less" scoped>
.tencent-map {
  width: 100%;
  min-height: 300px;
  height: 300px;
  position: relative;
}
.fixed-relation {
  position: relative;
}
/deep/ .smnoprint {
  display: none;
}
</style>
